import React from 'react';
import ConstituentQuickFilter from '@/constants/constituents/ConstituentQuickFilter';
import OpportunityFilter from '@/constants/opportunities/OpportunityQuickFilter';
import usePreselectedLabels from '@/lib/hooks/swr/usePreselectedLabels';
import { useQueryProvider } from '@/modules/QueryProvider';
import type OpportunitiesFilterParams from '../types/OpportunitiesFilterParams';

export default function useOpportunitiesFilterDefaultValues() {
  const { query } = useQueryProvider<OpportunitiesFilterParams>();

  const queryConstituentLabelIds: string[] = React.useMemo(
    () =>
      'constituentLabelIds' in query && Array.isArray(query.constituentLabelIds)
        ? query.constituentLabelIds
        : [],
    [query],
  );

  const { data: constituentLabels } = usePreselectedLabels(
    queryConstituentLabelIds,
  );

  const constituentDefaultValues = React.useMemo(
    () => ({
      [ConstituentQuickFilter.CONSTITUENT_TYPE]: query.constituentType,
      [ConstituentQuickFilter.CONSTITUENT_CATEGORIES]:
        query.constituentCategories,
      [ConstituentQuickFilter.CONSTITUENT_LABEL_IDS]: constituentLabels || [],
      [ConstituentQuickFilter.ASSIGNEE_IDS]: query.assigneeIds || '',
    }),
    [query, constituentLabels],
  );

  const queryOpportunityLabelIds: string[] = React.useMemo(
    () =>
      'opportunityLabelIds' in query && Array.isArray(query.opportunityLabelIds)
        ? query.opportunityLabelIds
        : [],
    [query],
  );

  const { data: opportunityLabels } = usePreselectedLabels(
    queryOpportunityLabelIds,
  );

  const opportunityDefaultValues = React.useMemo(
    () => ({
      [OpportunityFilter.ARCHIVED]:
        query.archived === null ? '' : query.archived,
      [OpportunityFilter.CAMPAIGN_ID]: query.campaignId || '',
      [OpportunityFilter.CONFIDENCE_LEVEL_IDS]: query.confidenceLevelIds || [],
      [OpportunityFilter.OPPORTUNITY_LABEL_IDS]: opportunityLabels || [],
      [OpportunityFilter.OPPORTUNITY_STAGE_IDS]:
        query.opportunityStageIds || [],
      [OpportunityFilter.OWNER_IDS]: query.ownerIds || [],
    }),
    [query, opportunityLabels],
  );

  return { constituentDefaultValues, opportunityDefaultValues };
}
